// Object-variable parser and declaration expander entry.
// Example: `var(--theme.accent)` key-resolution replacement.
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import { splitTopLevelDeclarations } from './split'
import { toCssPropertyName } from './keys'
import { expandObjectVariableBodyForLoops } from './body-for'
import type { ObjectVariableBodyForOptions } from './body-for'
import { replaceObjectValueReferences } from './references'
import { isReservedObjectKey, parseInlineObjectReference } from './parse-helpers'
// Strip wrapping quotes from resolved object values.
import { stripWrappingQuotes } from '../../loops/for-loop/selector'

// Detect object variable syntax by checking surrounding braces.
export function isObjectVariableValue(value: string): boolean {
  const trimmed = value.trim()
  // Return true when value is wrapped with `{ ... }`.
  return trimmed.startsWith('{') && trimmed.endsWith('}')
}

// Parse object variable content into a flat key/value map.
export function parseObjectVariable(
  value: string,
  objectVariables: LuisObjectVariables = {},
  loopOptions?: ObjectVariableBodyForOptions,
): Record<string, string> {
  const trimmed = value.trim()
  const inner = trimmed.slice(1, -1).trim()
  const result = Object.create(null) as Record<string, string>

  // Return empty map when object body is empty.
  if (!inner) {
    return result
  } else {
    // Continue parsing top-level declaration segments.
  }

  const expandedInner = loopOptions
    ? expandObjectVariableBodyForLoops(inner, loopOptions)
    : inner
  const chunks = splitTopLevelDeclarations(expandedInner)
  // Parse each `key: value` segment into the result map.
  for (const chunk of chunks) {
    const segment = chunk.trim()
    // Skip empty segments created by extra separators.
    if (!segment) {
      continue
    } else {
      // Continue parsing segment key/value split.
    }

    const colonIndex = segment.indexOf(':')
    // Parse inline object reference segments like `var(--layout);`.
    if (colonIndex === -1) {
      const inlineReference = parseInlineObjectReference(segment)
      // Skip non-reference segments without key/value shape.
      if (!inlineReference) {
        continue
      } else {
        // Continue merging referenced object entries.
      }

      const referencedObject = objectVariables[inlineReference]
      // Skip unknown object references and keep parser stable.
      if (!referencedObject) {
        continue
      } else {
        // Merge referenced object keys in-place for object spreading.
        for (const [entryKey, entryValue] of Object.entries(referencedObject)) {
          result[entryKey] = entryValue
        }
        continue
      }
    } else {
      // Continue key/value extraction.
    }

    const key = segment.slice(0, colonIndex).trim()
    const val = segment.slice(colonIndex + 1).trim()
    // Skip segments with missing key or missing value.
    if (!key || !val) {
      continue
    } else if (isReservedObjectKey(key)) {
      // Skip reserved keys to prevent prototype-pollution surfaces.
      continue
    } else {
      // Strip wrapping quotes from stored object values.
      result[key] = stripWrappingQuotes(val)
    }
  }

  // Return parsed object variable map.
  return result
}

// Expand declarations that reference object variables.
export function expandDeclarationWithObjectVariables(
  rawDeclaration: string,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables = {},
): string[] {
  const declaration = rawDeclaration.trim()
  // Ignore empty declarations after trimming whitespace.
  if (!declaration) {
    return []
  } else {
    // Continue object reference expansion checks.
  }

  // Regex matches full object-expansion form: `var(--objectName)`.
  const objectRef = declaration.match(/^var\(\s*(--[a-zA-Z_][\w-]*)\s*\)$/)
  // Expand all object entries when declaration is a full object reference.
  if (objectRef) {
    const objectName = objectRef[1]
    const objectValue = objectVariables[objectName]
    // Keep original declaration if referenced object is not defined.
    if (!objectValue) {
      return [declaration]
    } else {
      // Resolve nested object-key values before emitting expanded declarations.
      return Object.entries(objectValue).map(([key, val]) => {
        return `${toCssPropertyName(key)}: ${replaceObjectValueReferences(val, objectVariables, arrayVariables, parseObjectVariable)}`
      })
    }
  } else {
    // Return declaration with key-level replacements only.
    return [replaceObjectValueReferences(declaration, objectVariables, arrayVariables, parseObjectVariable)]
  }
}
